import {isInformationKey} from '@ulaw/content-types/resources';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContentError } from '@ulaw/content-types';
export const SITE = 'https://tuyensinh.hcmulaw.edu.vn/';
const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
export const endpoint = (path: string) => base + path;
export const privacyUrl = () => new URL(endpoint('/privacy'), window.location.href).href;
export const mediaUrl = (url: string) => endpoint('/api/media?url=' + encodeURIComponent(url));
export const pageRoute = (url: string) => '/read?url=' + encodeURIComponent(url);
const hosts = new Set(['tuyensinh.hcmulaw.edu.vn','ts.hcmulaw.edu.vn','sdh.hcmulaw.edu.vn','www.hcmulaw.edu.vn','hcmulaw.edu.vn']);
export function isInternal(url: string): boolean {
  if(isInformationKey(url))return true;
  try { const u = new URL(url); return ['http:','https:'].includes(u.protocol) && hosts.has(u.hostname) && !u.username && !u.password && !u.port && !/\.(docx?|xlsx?|zip|mp4)(\?|$)/i.test(u.pathname) && !/\/(admin|login|dang-nhap|register)(\/|$)/i.test(u.pathname); } catch { return false; }
}
export function isPdf(url: string): boolean {
  try { return !isInformationKey(url) && isInternal(url) && /\.pdf$/i.test(new URL(url).pathname); } catch { return false; }
}
export function useFresh<T>(path: string | null) {
  const [state,setState] = useState<{key:string|null; data?:T; error?:string; loading:boolean}>({key:path,loading:!!path});
  const seq = useRef(0), active = useRef<AbortController | null>(null);
  const refresh = useCallback(async () => {
    active.current?.abort(); const n = ++seq.current;
    if (!path) { setState({key:path,loading:false});return; }
    if (document.hidden) return;
    const controller = new AbortController(); active.current = controller;
    // Retain the last server snapshot while reloading the same route.
    setState(current=>current.key===path?{...current,loading:!current.data,error:undefined}:{key:path,loading:true});
    const timeout=setTimeout(()=>controller.abort(),25_000);
    try {
      const response = await fetch(endpoint(path),{cache:'no-store',signal:controller.signal});
      const result = await response.json();
      if (!response.ok || result.error) {
        const error = new Error((result as ContentError).error?.message || 'Bản nội dung chưa sẵn sàng.');
        if (response.status===410) setState({key:path,loading:false});
        throw error;
      }
      if(n===seq.current) setState({key:path,data:result,loading:false});
    } catch(error) {
      if(n===seq.current) setState(current=>({...current,key:path,error:error instanceof Error && error.name!=='AbortError'?error.message:'Chưa thể tải bản lưu. Vui lòng thử lại.',loading:false}));
    } finally {clearTimeout(timeout);}
  },[path]);
  useEffect(()=>{
    void refresh();
    const interval=setInterval(()=>void refresh(),60_000);
    const resume=()=>{if(!document.hidden)void refresh();else{active.current?.abort();seq.current++;}};
    window.addEventListener('focus',resume); window.addEventListener('pageshow',resume);document.addEventListener('visibilitychange',resume);
    return()=>{active.current?.abort();seq.current++;clearInterval(interval);window.removeEventListener('focus',resume);window.removeEventListener('pageshow',resume);document.removeEventListener('visibilitychange',resume);};
  },[refresh,path]);
  return {...(state.key===path?state:{key:path,loading:true}),refresh};
}
export const BOOKMARK_KEY='ulaw-bookmarks-v1';
export function readBookmarks(): string[] {
  try {const value=JSON.parse(localStorage.getItem(BOOKMARK_KEY)||'[]');return Array.isArray(value)?value.filter((v):v is string=>typeof v==='string'&&isInternal(v)).slice(0,100):[];}catch{return [];}
}
export function writeBookmarks(urls:string[]) { try {localStorage.setItem(BOOKMARK_KEY,JSON.stringify(urls.slice(0,100)));}catch{/* Current-session bookmarks remain usable when storage is unavailable. */} }
